/**
 * StudentSubmissionV2 - Individual Student Quiz Submission View with modern design
 * Features:
 * - Student dropdown to switch between students
 * - Question navigator with attempted/not attempted status
 * - Manual grading panel for subjective questions
 * - Answer reveal functionality
 * - V2 styling following LabsV2 design patterns
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { Breadcrumb, Spinner } from 'react-bootstrap';
import { Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as constants from 'Constants';
import httpService from 'services/httpService';
import displayGlobalMessage from 'Utils/DisplayGlobalMessage';
import log from 'loglevel';
import { useSelector } from 'react-redux';

import SubmissionHeader from './SubmissionHeader';
import QuestionNavigator from './QuestionNavigator';
import ManualGradingPanel from './ManualGradingPanel';
import QuestionCardWrapper from './QuestionCardWrapper';
import DocumentView from 'Screens1/Student/Course/Content/Multimedia/Components/DocumentView/DocumentView';

import styles from './StudentSubmissionV2.module.css';

const StudentSubmissionV2 = () => {
  const { courseId, labId, activityID, studentID } = useParams();
  const location = useLocation();

  // Get user mode to check if user is instructor
  const userMode = useSelector((state) => state.userInfo?.mode);
  const isInstructor = userMode === 'I';

  // Data state
  const [activityData, setActivityData] = useState(null);
  const [students, setStudents] = useState([]);
  // Decode the studentID from URL (it may be URL encoded)
  const decodedStudentID = studentID ? decodeURIComponent(studentID) : '';
  const [selectedStudent, setSelectedStudent] = useState(decodedStudentID);
  const [submissionData, setSubmissionData] = useState(null);
  const [rawSubmission, setRawSubmission] = useState(null);
  const [totalMarks, setTotalMarks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const studentsListFetched = useRef(false);

  // Update selectedStudent when URL param changes
  useEffect(() => {
    if (decodedStudentID && decodedStudentID !== selectedStudent) {
      setSelectedStudent(decodedStudentID);
    }
  }, [decodedStudentID]);

  // Question refs for scrolling
  const questionRefs = useRef([]);
  // Main content ref for preserving scroll position
  const mainContentRef = useRef(null);

  // Check if marks should be shown
  const showMarksToStudent = activityData?.show_marks_to_student;
  const shouldShowMarks = isInstructor || showMarksToStudent === true;

  // Fetch activity details
  const fetchActivityData = useCallback(async () => {
    try {
      const url = `${constants.base_url}/lab/activity/${activityID}?type=other`;
      const response = await httpService.get(url);
      const data = response.data?.other_activity;
      if (data) {
        setActivityData(data);
      }
    } catch (error) {
      log.error('Error fetching activity data:', error);
      displayGlobalMessage('Error loading activity data', 'error');
    }
  }, [activityID]);

  // Fetch all students list (with large page size to get all students)
  const fetchStudentsList = useCallback(async () => {
    try {
      const url = `${constants.base_url}/otherlabs/activity/${activityID}/other_activity_submissions/?page=1&page_size=500`;
      const response = await httpService.get(url);
      const data = response.data?.data || [];
      setStudents(data);
    } catch (error) {
      log.error('Error fetching students list:', error);
      displayGlobalMessage('Error loading students list', 'error');
    }
  }, [activityID]);

  const handleStudentsListLoad = useCallback(() => {
    if (!studentsListFetched.current) {
      studentsListFetched.current = true;
      fetchStudentsList();
    }
  }, [fetchStudentsList]);

  // Fetch specific student submission
  const fetchStudentSubmission = useCallback(async (email) => {
    if (!email) return;

    setSubmissionLoading(true);
    try {
      const url = `${constants.base_url}/otherlabs/activity/${activityID}/other_activity_submissions/${email}/`;
      const response = await httpService.get(url);
      const jsonData = response.data;
      const studData = jsonData.data;

      if (studData && studData[email]) {
        setTotalMarks(studData[email]['total_marks']);
        setRawSubmission({
          required_files: studData[email]['required_files'],
          responses: studData[email]['responses'] || [],
        });
      }
    } catch (error) {
      log.error('Error fetching student submission:', error);
      setRawSubmission({ required_files: [], responses: [] });
      setTotalMarks(null);
    } finally {
      setSubmissionLoading(false);
    }
  }, [activityID]);

  // Initial load - fire activity + submission requests in parallel
  useEffect(() => {
    let cancelled = false;
    const initLoad = async () => {
      setLoading(true);
      studentsListFetched.current = true;
      await Promise.allSettled([
        fetchActivityData(),
        selectedStudent ? fetchStudentSubmission(selectedStudent) : Promise.resolve(),
        fetchStudentsList(),
      ]);
      if (!cancelled) {
        setLoading(false);
      }
    };
    initLoad();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchActivityData, fetchStudentSubmission]);

  // Refetch submission when the selected student changes after the initial load
  const initialStudentRef = useRef(decodedStudentID);
  useEffect(() => {
    if (selectedStudent && selectedStudent !== initialStudentRef.current) {
      fetchStudentSubmission(selectedStudent);
    }
  }, [selectedStudent, fetchStudentSubmission]);

  // Merge raw submission into activity data once both are available
  useEffect(() => {
    if (!activityData || !rawSubmission) return;
    const updatedActivityData = { ...activityData };
    updatedActivityData.requiredfiles_set = rawSubmission.required_files;
    const responses = rawSubmission.responses || [];
    for (let i = 0; i < updatedActivityData?.question_set?.length; i++) {
      const question = updatedActivityData.question_set[i];
      const matchingResponse = responses.find(item => item.question === question.id);
      if (matchingResponse) {
        question.questionresponse_set = matchingResponse;
      } else {
        question.questionresponse_set = {};
      }
    }
    setSubmissionData(updatedActivityData);
  }, [activityData, rawSubmission]);

  // Handle student change from dropdown
  const handleStudentChange = (newStudentEmail) => {
    setSelectedStudent(newStudentEmail);
    // Update URL without full page reload
    const newUrl = location.pathname.replace(/\/[^/]+$/, `/${newStudentEmail}`);
    window.history.replaceState(null, '', newUrl);
  };

  // Scroll to question
  const scrollToQuestion = (index) => {
    if (questionRefs.current[index]) {
      questionRefs.current[index].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Update response callback (for grading) - preserves scroll position
  const updateResponse = async () => {
    // Save current scroll position
    const scrollTop = mainContentRef.current?.scrollTop || 0;

    // Refresh submission data after grading
    if (selectedStudent) {
      await fetchStudentSubmission(selectedStudent);

      // Restore scroll position after data loads
      setTimeout(() => {
        if (mainContentRef.current) {
          mainContentRef.current.scrollTop = scrollTop;
        }
      }, 0);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingWrapper}>
          <Spinner animation="border" />
          <span>Loading submission data...</span>
        </div>
      </div>
    );
  }

  const labTitle = activityData?.lab_data?.title || 'Lab';
  const activityTitle = activityData?.name || 'Activity';
  const questions = submissionData?.question_set || [];

  return (
    <div className={styles.pageContainer}>
      {/* Breadcrumb */}
      <Breadcrumb className={styles.breadcrumb}>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}/content/labs` }}>
          Labs
        </Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}/content/labs/${labId}` }}>
          {labTitle}
        </Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}/content/labs/${labId}/other/${activityID}` }}>
          {activityTitle}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Submissions</Breadcrumb.Item>
      </Breadcrumb>

      {/* Header */}
      <SubmissionHeader
        quizTitle={activityTitle}
        totalMarks={totalMarks}
        description={activityData?.description}
        students={students}
        selectedStudent={selectedStudent}
        onStudentChange={handleStudentChange}
        onStudentsDropdownFocus={handleStudentsListLoad}
        shouldShowMarks={shouldShowMarks}
      />

      {/* Main Content Area */}
      <div className={styles.contentWrapper}>
        {/* Left Side - Scrollable Questions */}
        <div className={styles.mainContent} ref={mainContentRef}>
          {submissionLoading ? (
            <div className={styles.submissionLoading}>
              <Spinner animation="border" size="sm" />
              <span>Loading student submission...</span>
            </div>
          ) : (
            questions.map((question, index) => (
              <div
                key={question.id || index}
                ref={(el) => (questionRefs.current[index] = el)}
                className={styles.questionWrapper}
              >
                <QuestionCardWrapper
                  question={question}
                  index={index}
                  courseId={courseId}
                  labId={labId}
                  activityData={activityData}
                  updateResponse={updateResponse}
                  shouldShowMarks={shouldShowMarks}
                  isInstructor={isInstructor}
                  studentEmail={selectedStudent}
                />
              </div>
            ))
          )}
        </div>

        {/* Right Sidebar - Fixed */}
        <div className={styles.sidebar}>
          <QuestionNavigator
            questions={questions}
            onQuestionClick={scrollToQuestion}
          />
          {shouldShowMarks && (
            <ManualGradingPanel
              questions={questions}
            />
          )}
          {/* Submitted Files Section */}
          {submissionData?.required_files_num > 0 && (
            <SubmittedFilesPanel
              activityData={submissionData}
              studentID={selectedStudent}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const toRelativeFilePath = (rawUrl) => {
  if (!rawUrl) return '';
  try {
    const parsed = new URL(rawUrl, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch (err) {
    return rawUrl;
  }
};

const SubmittedFilesPanel = ({ activityData, studentID }) => {
  const [previewFile, setPreviewFile] = useState(null);

  const handleDownloadFiles = async () => {
    try {
      const url = `${constants.base_url}/otherlabs/activity/${activityData.id}/other_submission_download_lab_files/?stud_id=${encodeURIComponent(studentID)}&username=${encodeURIComponent(studentID)}`;
      const response = await httpService.get(url, { responseType: 'blob' });

      const filename = `${studentID}_lab_${activityData.id}_files_.tar.gz`;
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      log.error('Error downloading files:', error);
      displayGlobalMessage('Failed to download files', 'error');
    }
  };

  const hasUploadedFiles = activityData?.requiredfiles_set?.filter(f => (f.isUploaded || f.id))?.length > 0;

  const isFilePreviewable = (file) => Boolean(file?.required_file) && file?.isUploaded !== false;

  const openPreview = (file) => {
    if (isFilePreviewable(file)) {
      setPreviewFile(file);
    }
  };

  const closePreview = () => setPreviewFile(null);

  const previewFileName = previewFile?.required_file_name || previewFile?.name || '';
  const previewSubSection = previewFile
    ? {
        data: {
          document_file: toRelativeFilePath(previewFile.required_file),
          title: previewFileName,
          description: '',
        },
      }
    : null;

  return (
    <div className={styles.submittedFilesPanel}>
      <div className={styles.panelHeader}>
        <h6>Submitted Files</h6>
        <small>(Required: {activityData?.required_files_num})</small>
      </div>
      {hasUploadedFiles ? (
        <>
          <button className={styles.downloadBtn} onClick={handleDownloadFiles}>
            Download All
          </button>
          <div className={styles.filesList}>
            <ol>
              {activityData?.requiredfiles_set?.map((file, index) => {
                const displayName = file?.name || file?.required_file_name;
                const canPreview = isFilePreviewable(file);
                if (!canPreview) {
                  return <li key={index}>{displayName}</li>;
                }
                return (
                  <li key={index}>
                    <button
                      type="button"
                      className={styles.fileLinkBtn}
                      onClick={() => openPreview(file)}
                    >
                      {displayName}
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>
        </>
      ) : (
        <div className={styles.noFiles}>No files submitted</div>
      )}
      <Dialog
        open={Boolean(previewFile)}
        onClose={closePreview}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle className={styles.previewDialogTitle}>
          <span className={styles.previewDialogTitleText}>{previewFileName}</span>
          <IconButton
            aria-label="Close preview"
            onClick={closePreview}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          className={styles.previewDialogContent}
          sx={{ overflow: 'hidden' }}
        >
          {previewSubSection && (
            <DocumentView selectedSubSection={previewSubSection} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentSubmissionV2;
